import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import InstantSearchProvider, {
  useInstantSearch,
} from '../InstantSearchProvider';
import {
  BRAND_SEARCH_INDEX_CREATED_AT_DESC,
  BRAND_SEARCH_INDEX_FEATURED,
} from '~constants/search';
import { authUserSelector } from '~redux/auth/selectors/authUser';
import { RootState } from '~redux/reducer';
import {
  clearedBrands,
  clearedBrandSearchState,
  BrandSearchSortType,
  recievedNbHits,
  recievedBrands,
  searchedBrands,
  selectBrandListFilterStrings,
  selectBrandListSortType,
  selectBrandListOffset,
  initializedBrandSearchList,
  selectBrandListCategory,
  selectBrandListQuery,
  selectIsBrandListInitialized,
  BrandListState,
} from '~redux/search/brand';
import { BrandReadModel } from '~types/api';
import { getPersonalizedBrands } from '~usecases/buyer/search/brand';

const BrandSearchIndex: Record<BrandSearchSortType, string> = {
  recommended: BRAND_SEARCH_INDEX_FEATURED,
  newest: BRAND_SEARCH_INDEX_CREATED_AT_DESC,
};

export const useBrandSearchSelector = <TSelected,>(
  selector: (state: RootState, listId: string) => TSelected,
  listId: string,
  compare?: (a: TSelected, b: TSelected) => boolean
) => {
  const memoizedSelector = useMemo(() => selector, []);
  return useSelector(
    (state: RootState) => memoizedSelector(state, listId),
    compare
  );
};

const BrandHitsProvider: React.FC<{ listId: string }> = React.memo(
  ({ listId, children }) => {
    const dispatch = useDispatch();
    const auth = useSelector(authUserSelector);
    const sort = useBrandSearchSelector(selectBrandListSortType, listId);
    const {
      hits,
      searching,
      isSearchStalled,
      searchResults,
    } = useInstantSearch<BrandReadModel>();

    useEffect(() => {
      if (searching || isSearchStalled) {
        dispatch(searchedBrands(listId));
        return () => {
          dispatch(clearedBrands(listId));
        };
      }
      if (!auth || sort !== 'recommended' || !hits.length) {
        dispatch(recievedBrands({ listId, brands: hits }));
        return;
      }
      dispatch(
        getPersonalizedBrands({
          accountId: auth.attributes.sub,
          listId,
          brands: hits,
        })
      );
    }, [
      auth,
      JSON.stringify(hits.map((hit) => hit.id)),
      searching,
      isSearchStalled,
    ]);

    useEffect(() => {
      if (!searchResults) {
        return;
      }
      dispatch(recievedNbHits({ listId, nbHits: searchResults.nbHits }));
    }, [searchResults?.nbHits]);

    return <>{children}</>;
  }
);

const BrandSearchListProvider: React.FC<{ listId: string }> = React.memo(
  ({ listId, children }) => {
    const sort = useBrandSearchSelector(selectBrandListSortType, listId);
    const indexName = useMemo(() => BrandSearchIndex[sort ?? 'recommended'], [
      sort,
    ]);
    const brandFilters = useBrandSearchSelector(
      selectBrandListFilterStrings,
      listId,
      (a, b) => {
        return JSON.stringify(a) === JSON.stringify(b);
      }
    );
    const category = useBrandSearchSelector(selectBrandListCategory, listId);
    const query = useBrandSearchSelector(selectBrandListQuery, listId);
    const filters = useMemo(
      () =>
        `category_page_id:'${category ? `brands > ${category}` : 'brands'}'${
          brandFilters.length ? ` AND (${brandFilters.join(') AND (')})` : ''
        }`,
      [category, JSON.stringify(brandFilters)]
    );
    const offset = useBrandSearchSelector(selectBrandListOffset, listId);
    const auth = useSelector(authUserSelector);
    const attributesToRetrieve = useMemo(() => {
      const attributes = ['*'];
      return attributes;
    }, [auth]);

    return (
      <InstantSearchProvider
        indexName={indexName}
        query={query}
        filters={filters}
        offset={offset}
        length={500}
        attributesToRetrieve={attributesToRetrieve}
      >
        <BrandHitsProvider listId={listId}>{children}</BrandHitsProvider>
      </InstantSearchProvider>
    );
  }
);

type BrandSearchProviderProps = {
  listId: string;
} & Partial<BrandListState>;

const BrandSearchProvider: React.FC<BrandSearchProviderProps> = React.memo(
  ({ listId, children, ...props }) => {
    const dispatch = useDispatch();
    const isInitialized = useBrandSearchSelector(
      selectIsBrandListInitialized,
      listId
    );

    useEffect(() => {
      dispatch(initializedBrandSearchList({ listId, ...props }));
      return () => {
        dispatch(clearedBrandSearchState(listId));
      };
    }, [listId]);

    if (!isInitialized) {
      return null;
    }

    return (
      <BrandSearchListProvider listId={listId}>
        {children}
      </BrandSearchListProvider>
    );
  }
);

export default BrandSearchProvider;
